import React, { useCallback, useEffect, useRef, useState } from "react"

import { gql, useMutation, useQuery } from "urql"

import { SelectOption } from "~tailwindui/types/SelectOption"

import Panel from "~tailwindui/Basics/Panel"

import EventEmitter from "src/EventEmitter"
import classNames from "src/classNames"
import { RunMutation } from "src/types"

import useIsMobile from "hooks/useIsMobile"

import UrqlLoadingIndicator from "components/WithLoadingIndicator/urql"

import OpenThread from "./OpenThread"
import ThreadList from "./ThreadList"

export type User = {
  id: string
  type: "Teacher" | "Tutor" | "Student" | "Parent"
  messagingDisplayName: string
  firstName: string
  avatar: {
    thumbUrl: string
  }
}

export type MessageThread = {
  id: string
  title: string
  messagesCount: number
  unreadMessagesCount: number
  createdAt: string
  users: User[]
  messages: {
    totalPages: number
    currentPage: number
    data: {
      id: string
      read: boolean
      content: string
      createdAt: string
      contentFlagged: boolean
      author: User
    }[]
  }
  author: User
}

const Messaging: React.FC = () => {
  const isMobile = useIsMobile(1024)

  const [page, setPage] = useState(1)
  const [userFilter, setUserFilter] = useState<undefined | SelectOption>()

  const [result, refreshQuery] = useQuery({
    query: threadsQuery,
    variables: { page, userIdFilter: userFilter?.value },
  })
  const [, runReadMutation]: [any, RunMutation] = useMutation(markReadMutation)

  const [openThread, setOpenThread] = useState<MessageThread | null>()
  const threadIdRef = useRef<string | null>(openThread?.id)

  const markRead = useCallback(() => {
    if (threadIdRef.current) {
      runReadMutation({ messageThreadId: threadIdRef.current }).then(
        refreshQuery
      )
    }
  }, [runReadMutation, refreshQuery])

  // keep threadIdRef current
  useEffect(() => {
    threadIdRef.current = openThread?.id
  }, [openThread])

  // auto-open first thread (desktop only)
  useEffect(() => {
    if (isMobile || !result.data || openThread) return
    setOpenThread(result.data.viewer.messageThreads.data[0])
  }, [isMobile, result.data, openThread])

  // keep openThread up to date
  useEffect(() => {
    if (!result.data || !openThread) return

    const updatedThread = result.data.viewer.messageThreads.data.find(
      thread => thread.id === openThread.id
    )
    setOpenThread(updatedThread)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result.data])

  // mark read when a new thread is opened
  useEffect(() => {
    if (!openThread) return
    if (openThread.unreadMessagesCount > 0) {
      const timeout = setTimeout(markRead, 2000)
      return () => clearTimeout(timeout)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openThread?.id])

  // mark open thread read 2 seconds after message is received
  useEffect(() => {
    const handleEvent = () => {
      refreshQuery()
      setTimeout(markRead, 2000)
    }
    EventEmitter.subscribe("messageReceived", handleEvent)

    return () => {
      EventEmitter.unsubscribe("messageReceived", handleEvent)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // reset threads page on filter change
  useEffect(() => {
    setPage(1)
  }, [userFilter])

  return (
    <div className="w-full">
      <UrqlLoadingIndicator result={result}>
        {({ data: { viewer } }) => (
          <div className="flex h-[calc(100vh-130px)] w-full flex-col gap-4 md:flex-row">
            <Panel
              className={classNames(
                "mb-0 flex min-w-0 flex-1 flex-col overflow-hidden p-0",
                "h-full overflow-y-auto md:max-w-xs",
                openThread && "hidden md:block"
              )}
            >
              <ThreadList
                viewer={viewer}
                openThread={openThread}
                setOpenThread={setOpenThread}
                userFilter={userFilter}
                setUserFilter={setUserFilter}
                refreshQuery={refreshQuery}
              />
            </Panel>

            <Panel
              className={classNames(
                "mb-0 flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden p-0",
                !openThread && "hidden md:block"
              )}
            >
              {openThread ? (
                <OpenThread
                  viewer={viewer}
                  openThread={openThread}
                  setOpenThread={setOpenThread}
                />
              ) : (
                <div className="p-5 italic">No threads to display.</div>
              )}
            </Panel>
          </div>
        )}
      </UrqlLoadingIndicator>
    </div>
  )
}

const threadsQuery = gql`
  query ThreadsQuery($page: Int!, $userIdFilter: ID) {
    viewer {
      id
      firstName
      messagingDisplayName
      avatar {
        thumbUrl
      }
      messageThreads(page: $page, userIdFilter: $userIdFilter) {
        currentPage
        totalPages
        data {
          id
          title
          messagesCount
          unreadMessagesCount
          createdAt
          users {
            id
            type
            firstName
            messagingDisplayName
            avatar {
              thumbUrl
            }
          }
          messages {
            totalPages
            currentPage
            data {
              id
              read
              content
              createdAt
              contentFlagged
              author {
                id
                type
                firstName
                messagingDisplayName
                avatar {
                  thumbUrl
                }
              }
              attachment {
                filename
                contentType
                downloadUrl
                url
                thumbUrl
                previewUrl
              }
            }
          }
          author {
            id
            type
            firstName
            messagingDisplayName
            avatar {
              thumbUrl
            }
          }
        }
      }
    }
  }
`

const markReadMutation = gql`
  mutation MarkRead($messageThreadId: ID!) {
    messageThreadMarkRead(messageThreadId: $messageThreadId) {
      failures {
        message
      }
    }
  }
`

export default Messaging
